// RealtimeChat.js – React WebRTC client that
//  • streams mic → OpenAI Realtime
//  • receives assistant audio + transcript
//  • logs each final turn to /chat/realtime-log

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './RealtimeChat.css';
import { NotificationManagerError } from '../utils/Notification';

export default function RealtimeChat() {
  const navigate   = useNavigate();
  const location   = useLocation();

  /* ───────── UI state ───────── */
  const [status, setStatus]                 = useState('connecting…');
  const [isListening, setListen]            = useState(false);
  const [isBlockedByLimit, setIsBlockedByLimit] = useState(false);

  /* ───────── refs ───────── */
  const pcRef               = useRef(null);
  const dcRef               = useRef(null);
  const chatIdRef           = useRef(null);
  const audioSenderRef      = useRef(null);
  const currentAssistantRef = useRef('');
  const currentUserRef      = useRef('');
  const conversationLogRef  = useRef([]);
  const bootedRef           = useRef(false);          // <-- Strict-mode guard

  /* ───────── helper: persist turn ───────── */
  async function persistTurn(role, text) {
    if (isBlockedByLimit) return;
    const raw = localStorage.getItem('tokens');
    if (!raw) return;
    const token = JSON.parse(raw);

    try {
      const resp = await fetch(
        // 'http://localhost:4005/ai-therapist/chat/realtime-log',
        'https://therapywithai.com/ai-therapist/chat/realtime-log',
        {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
            Authorization  : `Bearer ${token.access ?? token}`,
          },
          body: JSON.stringify({ chatId: chatIdRef.current, role, text }),
        }
      );

      if (resp.status === 429) {
        setIsBlockedByLimit(true);
        setListen(false);
        NotificationManagerError(
          <>
            <a
              href="https://therapywithai.com/plans-pricing"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              Daily limit reached – upgrade for unlimited access.
            </a>
          </>
        );

        if (pcRef.current && audioSenderRef.current) {
          try {
            pcRef.current.removeTrack(audioSenderRef.current);
            audioSenderRef.current = null;
          } catch (e) {
            console.error('[RTC] removeTrack failed', e);
          }
        }
      } else if (!resp.ok) {
        console.error('[LOG → backend] status', resp.status);
      }
    } catch (err) {
      console.error('[LOG → backend] network', err);
    }
  }

  /* ───────── helper: local log + persist ───────── */
  function logConversation(role, text) {
    if (role === 'user' && isBlockedByLimit) return;
    conversationLogRef.current.push({ role, text, ts: Date.now() });
    if (role !== 'system') persistTurn(role, text);
  }

  /* ───────── bootstrap (runs ONCE) ───────── */
  useEffect(() => {
    if (bootedRef.current) return;          // prevent 2nd run in React-18
    bootedRef.current = true;

    (async () => {
      try {
        /* 1️⃣ auth */
        const rawTok = localStorage.getItem('tokens');
        if (!rawTok) throw new Error('no auth token');
        const authTok = JSON.parse(rawTok);

        /* 2️⃣ figure out which chatId to request */
        const urlId  = new URLSearchParams(location.search).get('id');
        const stored = localStorage.getItem('primaryRealtimeChatId');
        const query  = urlId ? `?chatId=${urlId}` : '';

        /* 3️⃣ /session */
        const sessRes = await fetch(
          `https://therapywithai.com/session${query}`,
          { headers: { Authorization: `Bearer ${authTok.access ?? authTok}` } }
        );
        if (!sessRes.ok) throw new Error(`/session ${sessRes.status}`);
        const { client_secret, chatId } = await sessRes.json();

        /* 4️⃣ persist chosen chatId & fix URL if needed */
        chatIdRef.current = chatId;
        localStorage.setItem('primaryRealtimeChatId', chatId);
        if (!urlId || urlId !== chatId)
          navigate(`/realtime-chat?id=${chatId}`, { replace: true });

        /* 5️⃣ WebRTC */
        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        /* remote audio */
        const remoteAudio = new Audio();
        remoteAudio.autoplay = true;
        pc.ontrack = e => (remoteAudio.srcObject = e.streams[0]);

        /* microphone */
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioSenderRef.current = pc.addTrack(stream.getAudioTracks()[0], stream);
        setListen(true);

        /* data channel */
        const dc = pc.createDataChannel('oai-events');
        dcRef.current = dc;
        dc.onopen = () => {
          setStatus('connected');
          logConversation('system', 'Connection established');
        };

        /* OpenAI realtime message handler */
        dc.onmessage = ({ data }) => {
          let msg;
          try { msg = JSON.parse(data); } catch { return; }

          if (msg.type === 'session.created') {
            dc.send(
              JSON.stringify({
                type   : 'session.update',
                session: { input_audio_transcription: { model: 'whisper-1' } },
              })
            );
            return;
          }

          /* USER transcription */
          if (msg.type === 'input_audio_buffer.speech_started') {
            currentUserRef.current = '';
            return;
          }
          if (msg.type === 'conversation.item.input_audio_transcription.delta') {
            currentUserRef.current += msg.delta?.text ?? msg.delta ?? '';
            return;
          }
          if (msg.type === 'conversation.item.input_audio_transcription.completed') {
            const finalText =
              (msg.transcript ?? '').trim() || currentUserRef.current.trim();
            if (finalText) logConversation('user', finalText);
            currentUserRef.current = '';
            return;
          }

          /* ASSISTANT */
          if (msg.type === 'response.audio_transcript.delta') {
            currentAssistantRef.current += msg.delta ?? '';
            return;
          }
          if (msg.type === 'response.done') {
            if (currentAssistantRef.current) {
              logConversation('assistant', currentAssistantRef.current.trim());
              currentAssistantRef.current = '';
            }
            return;
          }
        };

        /* SDP handshake */
        await pc.setLocalDescription(await pc.createOffer());
        const answerSDP = await fetch(
          'https://api.openai.com/v1/realtime?model=gpt-4o-realtime-preview-2024-12-17',
          {
            method : 'POST',
            headers: {
              Authorization: `Bearer ${client_secret.value}`,
              'Content-Type': 'application/sdp',
            },
            body: pc.localDescription.sdp,
          }
        ).then(r => r.text());

        await pc.setRemoteDescription({ type: 'answer', sdp: answerSDP });
        console.log('[RTC] connected');
      } catch (err) {
        console.error('[BOOT] error', err);
        setStatus(`error: ${err.message}`);
        logConversation('system', `Error: ${err.message}`);
      }
    })();

    /* cleanup */
    return () => {
      try {
        dcRef.current?.close();
        pcRef.current?.close();
      } catch {}
      if (audioSenderRef.current) {
        audioSenderRef.current.track?.stop?.();
      }
    };
  }, []);

  /* ───────── UI ───────── */
  return (
    <div
      className={`realtime-chat-container ${
        isBlockedByLimit ? 'blocked-by-limit' : ''
      }`}
    >
      <button
        onClick={() => navigate('/')}
        className="back-home-button"
        title="Back to Home"
      >
        &lt;
      </button>

      <div className="logo-container">
        <img
          src="/assets/images/logo.png"
          alt="Therapy with AI"
          className="logo"
        />
      </div>

      <div className="status-container">
        <div
          className={`status ${
            status === 'connected'
              ? 'connected'
              : status.startsWith('error')
              ? 'error'
              : ''
          }`}
        >
          {status}
        </div>
      </div>

      <div className="avatar-container">
        <div className="avatar">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="10" />
          </svg>
        </div>
        <div className="instructions">
          {isBlockedByLimit
            ? 'Daily limit reached'
            : isListening
            ? 'Listening…'
            : status}
        </div>
      </div>
    </div>
  );
}
